import { Header } from '@components/Header';
import AddIcon from '@components/icons/Add.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { useAPI } from '@providers/useAPI';
import { DeadlineCalendar } from '@pages/User/DashboardPage/DeadlineCalendar';
import { JobsList } from '@pages/User/ScriberDashboardPage/JobsList';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import { dashboardKeys } from 'app/queries/dashboard/dashboardKeys';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { createFetchScriberDashboardInfoQueryFn } from './api/fetchScriberDashboardInfo';
import { Wrapper } from './styles/Wrapper';

export const ScriberDashboardPage = () => {
  const { call } = useAPI();
  const navigate = useNavigate();
  const { organization, workspace, me } = useAuthContext();
  const organizationId = organization?.id ?? '';

  const dashboardQuery = useQuery({
    queryKey: dashboardKeys.scriberInfo(organizationId),
    queryFn: createFetchScriberDashboardInfoQueryFn(call),
    enabled: organizationId !== '',
  });
  const dashboardInfo = dashboardQuery.data;
  const isLoading = dashboardQuery.isLoading;

  if (!me.organizations.length) {
    navigate(routes.accountPage.make());
    return null;
  }

  const handleAddJob = () => {
    navigate(routes.newJob.make(organization?.slug ?? '', workspace?.slug ?? ''));
  };

  const headerLeftSideContent = [];
  if (organization?.permissions?.submitJobs) {
    headerLeftSideContent.push(
      <OutlinedButton
        key="button"
        className="w-[130px] gap-[5px]"
        data-testid="create_job_header_button"
        onClick={handleAddJob}
      >
        <AddIcon height="12px" width="12px" />
        Create New Job
      </OutlinedButton>,
    );
  }

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Dashboard - AutoScript</title>
        </Helmet>
        <Header title="Dashboard" leftSideContent={headerLeftSideContent} />

        <LinearProgressLoader active={isLoading} />
        <Wrapper></Wrapper>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - AutoScript</title>
      </Helmet>
      <Header title="Dashboard" leftSideContent={headerLeftSideContent} />

      <Wrapper>
        <div className="left-wrapper">
          <div className="line">
            <div className="card">
              <div className="title">My Efficiency</div>
              <div className="subTitle">{dashboardInfo?.myJobs?.length || 0} Active Jobs</div>
              <div className="description">Completed</div>
              <div className="description">{dashboardInfo?.completedJobs || 0}</div>
            </div>

            <div className="card">
              <div className="title">Number of Jobs to Claim</div>
              <div className="subTitle">{dashboardInfo?.jobsToClaim?.length || 0} Available</div>
              <div className="description">Total Limit</div>
              <div className="description">
                {dashboardInfo?.concurrentJobsLimit
                  ? `${dashboardInfo?.concurrentJobsLimit} concurrent jobs`
                  : 'Not set'}
              </div>
            </div>
          </div>
          <div className="line">
            <div className="card card-jobsList">
              <div className="superTitle">My Jobs List</div>
              <JobsList jobs={dashboardInfo?.myJobs ?? []} />
            </div>
            <div className="card card-jobsList">
              <div className="superTitle">Jobs Available to Claim</div>
              <JobsList jobs={dashboardInfo?.jobsToClaim ?? []} />
            </div>
          </div>
        </div>
        <div className="card card-deadline">
          <div className="superTitle">Deadline Calendar</div>
          <DeadlineCalendar calendar={dashboardInfo?.deadlineCalendar ?? []} />
        </div>
      </Wrapper>
    </>
  );
};
